import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaPlay,
  FaCircle,
  FaFileAlt,
  FaChartBar,
  FaExchangeAlt,
  FaRobot,
  FaCode,
} from "react-icons/fa";
import { Alert, AlertType } from "@/models/alert";
import { createAlertDragPayload } from "@/utils/drag-types";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import SidebarItem, { SidebarItemAction } from "./sidebar-item";
import ResourceContextMenu from "./resource-context-menu";

// Icon mapping for alert types
const ALERT_TYPE_ICONS: Record<AlertType, React.ReactNode> = {
  new_documents: <FaFileAlt size={12} />,
  threshold: <FaChartBar size={12} />,
  change: <FaExchangeAlt size={12} />,
  anomaly: <FaRobot size={12} />,
  custom: <FaCode size={12} />,
};

// Color mapping for alert types
const ALERT_TYPE_COLORS: Record<AlertType, string> = {
  new_documents: "text-blue-500",
  threshold: "text-emerald-500",
  change: "text-amber-500",
  anomaly: "text-purple-500",
  custom: "text-rose-500",
};

interface AlertItemProps {
  alert: Alert;
  onRunAlert?: (alert: Alert) => void;
  // Multi-select props
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Ordered list of all alert IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
  // Drag-to-group props
  /** Callback when another alert is dropped on this one to create a group */
  onDropToCreateGroup?: (draggedAlertId: string, targetAlertId: string) => void;
}

/**
 * AlertItem component - displays an alert in the sidebar.
 * Shows the alert's name, type icon, and status indicator.
 * Supports drag-and-drop to create groups by dropping one alert on another.
 */
const AlertItem: React.FC<AlertItemProps> = ({
  alert,
  onRunAlert,
  isSelected,
  orderedIds = [],
  onMultiSelectContextMenu,
  onDropToCreateGroup,
}) => {
  // Panel navigation hook for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const isEnabled = alert.enabled;
  const typeIcon = ALERT_TYPE_ICONS[alert.config.type] || ALERT_TYPE_ICONS.custom;
  const typeColor = ALERT_TYPE_COLORS[alert.config.type] || ALERT_TYPE_COLORS.custom;

  // Create drag payload
  const dragPayload = createAlertDragPayload(alert.id, alert.name);

  const handleClick = useCallback(() => {
    navigateTo(`/alerts?id=${alert.id}`, { title: alert.name });
  }, [alert.id, alert.name, navigateTo]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleRun = useCallback(() => {
    if (isEnabled) {
      onRunAlert?.(alert);
    }
  }, [alert, isEnabled, onRunAlert]);

  // Status indicator
  const statusIndicator = (
    <FaCircle
      size={6}
      className={isEnabled ? "text-green-500" : "text-gray-400"}
      title={isEnabled ? "Activée" : "Désactivée"}
    />
  );

  // Build hover actions
  const hoverActions = onRunAlert && isEnabled ? (
    <SidebarItemAction
      onClick={handleRun}
      title="Exécuter"
      icon={<FaPlay size={10} />}
      variant="success"
    />
  ) : null;

  return (
    <>
      <SidebarItem
        id={alert.id}
        name={alert.name}
        resourceType="alert"
        icon={<span className={typeColor}>{typeIcon}</span>}
        isDisabled={!isEnabled}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        enableDrag={true}
        onDropToCreateGroup={onDropToCreateGroup}
        dragPayload={dragPayload}
        hoverActions={hoverActions}
        statusIndicator={statusIndicator}
      />

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceContextMenu
            position={contextMenu}
            dragPayload={dragPayload}
            onClose={closeContextMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
};

AlertItem.displayName = "AlertItem";

export default AlertItem;
